import React, { useEffect, useState } from 'react';
import axios from 'axios';
import InvoiceItem from './components/invoiceItem';
import { useNavigate } from 'react-router-dom';
import LoadingSpinner from './components/LoadingSpinner';
import './customerInvoices.css';
import { getPrevMonthStartDate, getPrevMonthEndDate, formatDateToZoho } from './components/utils';
import { writeFileXLSX,utils } from 'xlsx';
import { isAuthTokenValid } from './components/utils';
import { FaPencilAlt, FaTimes } from 'react-icons/fa'

const CustomerInvoices = ({customerName}) => {
  const [invoices, setInvoices] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [groupedInvoices, setGroupedInvoices] = useState({});
  const [overallTotal, setOverallTotal] = useState(0);
  const [totalDiscount, setTotalDiscount] = useState(0);
  const [progress, setProgress] = useState(0);
  const [dateRangeText, setDateRangeText] = useState('');
  const [editingInvoiceId, setEditingInvoiceId] = useState(null);
  const [modifiedPatientNames, setModifiedPatientNames] = useState({});
  const [modifiedPatientIDs, setModifiedPatientIDs] = useState({});
  const [modifiedDisplayNames,setModifiedDisplayNames] = useState({});

  const orgID = '837447409';
  const serverURL = process.env.REACT_APP_SERVER_BASE_URL;
  const navigate = useNavigate();

  useEffect(() => {
    setIsLoading(true);

    if(customerName) {
        fetchAllInvoices();
    }
  }, [customerName]);

  const fetchAllInvoices = async () => {
    const accessToken = localStorage.getItem('access_token');
    if (!isAuthTokenValid()) {
        navigate('/authentication');
        return;
      }
    const preferences = JSON.parse(localStorage.getItem('userPreferences'));
    let startDate = getPrevMonthStartDate();
    let endDate = getPrevMonthEndDate();
    let status = 'draft';
    // overide default values if user changed them
    if (preferences) {
        startDate =  preferences.startDate || '';
        endDate = preferences.endDate || '';
        status = preferences.invoiceStatus || 'draft';
      }
     setDateRangeText(formatDateToZoho(startDate)+' - '+formatDateToZoho(endDate));
    let allInvoices = [];
    let hasMorePages = true;
    let pageNumber = 1;
  
    while (hasMorePages) {
      const { returnedInvoices, hasMorePages: nextPage } = await fetchData(pageNumber,startDate,endDate,accessToken);
      allInvoices = [...allInvoices, ...returnedInvoices];
      hasMorePages = nextPage;
      pageNumber++;
    }
    const returnedNames = await fetchSavedNames();
    setModifiedDisplayNames(returnedNames);
    setInvoices(allInvoices);
  };

  const fetchSavedNames = async () => {
    try {
        const response = await axios.get(serverURL + `/sh_all_invoices`);
        if(response.status === 200) {
            const invoices = response.data; // Assuming response data is an array of invoices
            const savedNames = {};
            invoices.forEach(invoice => {
                const { invoice_id, patient_name, patient_id } = invoice;
                savedNames[invoice_id] = { patient_name, patient_id };
            });
            return savedNames;
        } else {
            console.error('API error:', response.status, response.data);
            return {};
        }

    } catch(error) {
        console.error('Error fetching saved names:', error);
        return {};
    }
}

  const fetchData = async (pageNumber = 1,startDate,endDate,accessToken) => {
    
    try {
      let params = {
        customer_name: customerName,
        organization_id: orgID,
        page: pageNumber // Pass the page number parameter
      };
  
      // Check if startDate and endDate preferences are stored
      if (startDate && endDate) {
        params.date_start = startDate;
        params.date_end = endDate;
      }
  
      console.log('start date:', startDate, ' end date: ', endDate);

      const response = await axios.get(serverURL+'/getInvoices', {
        params: params,
        headers: {
          Authorization: 'Zoho-oauthtoken '  + accessToken,
        },
      });
      if (response.status >= 200 && response.status < 300) {
        const responseData = response.data;
        const returnedInvoices = responseData.response.invoices;
        const hasMorePages = responseData.response.page_context.has_more_page;

         return {returnedInvoices,hasMorePages};
 
      } else {
        console.error('API error:', response.status, response.statusText);
        return { returnedInvoices: [], hasMorePages: false };
      }
    } catch (error) {
      console.error('Error fetching invoices:', error);
      // Reset token data stored in localStorage on error
      localStorage.removeItem('access_token');
      navigate('/authentication');
      return { returnedInvoices: [], hasMorePages: false };
    }
  };


  useEffect(() => {
    setGroupedInvoices({}); //make sure the list is clear.
    const groupData = async () => {
        //console.log('Entered grouping data on ',new Date());
        const grouped = {};
        let completedCount = 0;
      for (const invoice of invoices) {
        const accessToken = localStorage.getItem('access_token');
        const { invoice_id } = invoice;
        completedCount = completedCount==0?1:completedCount;//if first time around trigger the loading spinner
        setProgress((completedCount / invoices.length) * 100);
        // Introduce a delay of 2 seconds (2000 milliseconds) before making the API call
        await new Promise(resolve => setTimeout(resolve, 1000));
  
        try {
          const response = await axios.get(serverURL+`/getInvoice`, {
            params: {
              organization_id: orgID,
              invoice_id: invoice_id,
            },
            headers: {
                Authorization: 'Zoho-oauthtoken '  + accessToken,
            },
          });
          if (response.status >= 200 && response.status < 300) {
            const responseData = response.data;
            const lineItems = responseData.response.invoice.line_items;
            
            let patientName = '';
            let patientID = '';
                const customFields = invoice.custom_fields;
                const patientNameField = customFields.find((field) => field.label === 'Patient Name');
                const patientIDField = customFields.find((field) => field.label === 'Patient ID');
                patientID = patientIDField ? patientIDField.value : ''; // Check if patientIDField exists
                patientName = patientNameField.value;

            if (patientID && !patientID.includes('ID')) {
                patientID = 'ID-' + patientID;
            }
            
            const key = patientName + " (" + patientID + ")";

            if (!grouped[key]) {
              grouped[key] = [];
            }
            grouped[key].push({ invoice, lineItems });
            //console.log(new Date(), 'rendered invoice ', invoice_id);
            completedCount++;
            
          
          } else {
            console.error('API error:', response.status, response.statusText);
          }
        } catch (error) {
          console.error('Error fetching invoice details:', error);
        }
      }
      setGroupedInvoices(grouped);
      setIsLoading(false);
    };
  
    if (invoices.length > 0) {
      groupData();
    }
  }, [invoices]);
  

  useEffect(() => {
    const calculateOverallTotal = () => {
      const total = invoices.reduce((acc, invoice) => acc + invoice.total, 0);
      const discount = invoices.reduce((acc, invoice) => acc + invoice.adjustment, 0);
      setOverallTotal(total);
      setTotalDiscount(discount);
    };

    if (invoices.length >= 0) {
      calculateOverallTotal();
    }
  }, [invoices]);

  const handlePrint = () => {
    const invoicesElement = document.getElementById('list-invoices');
    if (invoicesElement) {
      const originalDisplayStyle = invoicesElement.style.display;
      invoicesElement.style.display = 'block';
      window.print();
      invoicesElement.style.display = originalDisplayStyle;
    } else {
      window.print();
    }
  };

const handleExport = () => {
    const wb = utils.book_new();
    const patientData = [];
  
    Object.keys(groupedInvoices).forEach((key) => {
      const lineItemTotals = {}; // Object to store totals for each LineItemByGroup
  
      groupedInvoices[key].forEach(({ invoice, lineItems }) => {
        lineItems.forEach((item) => {
          let itemKey;
          let lineItemTotal = parseFloat(item.item_total); // Parse item total to a float
  
          if (item.name.includes('Consultation')) {
            itemKey = 'Consultation Fees';
          } else if (item.name.includes('Admission')) {
            itemKey = 'Admission Fees';
          } else if (item.name.includes('Observation')) {
            itemKey = 'Observation Fees';
          } else {
            itemKey = 'Lab and Medication';
          }
  
          // Aggregate item total per group
          lineItemTotals[itemKey] = (lineItemTotals[itemKey] || 0) + lineItemTotal;
        });
      });
  
      // Create a new row for each LineItemByGroup entry
      Object.entries(lineItemTotals).forEach(([itemKey, total]) => {
        const patientName = getDisplayName(groupedInvoices[key][0].invoice.invoice_id,key);
        const rowData = [patientName, groupedInvoices[key][0].invoice.date, itemKey, total, groupedInvoices[key][0].invoice.total]; // Duplicate key, total, and date, add itemKey and total
        patientData.push(rowData);
      });
    });
  
    const ws = utils.aoa_to_sheet([
      ['Patient Name', 'Date', 'Description', 'Sub Total','Invoice Total'],
      ...patientData
    ]);

    ws['!cols'] = [{ width: 40 }, { width: 15 }, { width: 30 }, { width: 15 }, { width: 15 }]; // Set column widths

   utils.book_append_sheet(wb, ws, 'Patients');
  
   const fileName = getExportFileName();

    writeFileXLSX(wb, fileName);
  };

  const getExportFileName = () => {
        // Sanitize the customerName for use in a file name
        const sanitizedCustomerName = customerName.replace(/[^\w\s]/gi, ''); // Remove all non-alphanumeric characters

        // Get the current date in the format yyyymmdd
        const currentDate = new Date().toISOString().slice(0, 10).replace(/-/g, '');

        // Concatenate the sanitized customerName and currentDate to form the file name
        const fileName = sanitizedCustomerName + '_' + currentDate + '.xlsx';

        return fileName;
    };
    const handleEditPatientName = (invoiceId) => {
        setEditingInvoiceId(invoiceId);
    };
    
    const handleCancelPatientNameEdit = (invoiceId) => {
        setEditingInvoiceId(null);
    }

    const handleSavePatientName = async (invoiceId) => {
        // Save modified patient name to the database
        const patientName = modifiedPatientNames[invoiceId];
        const patientID = modifiedPatientIDs[invoiceId];
        try {
            // Check if a record exists for the given invoiceId
            const existingRecordResponse = await axios.get(serverURL + `/sh_invoice-patient/${invoiceId}`);
            if (existingRecordResponse.status === 200 && existingRecordResponse.data) {
                // Record exists, update it
                const response = await axios.put(serverURL + `/sh_invoice-patient/${invoiceId}`, {
                    patient_name:patientName,
                    patient_id:patientID,
                    invoice_number: '1234'
                });
                if (response.status === 200) {
                    console.log('Record updated successfully');
                } else {
                    console.error('Failed to update record');
                }
            } else  {
                // Record does not exist, add it
                const response = await axios.post(serverURL + '/sh_invoice-patient', {
                    patient_name:patientName,
                    patient_id:patientID,
                    invoice_number: '1234',
                    invoice_id: invoiceId
                });
                if (response.status === 200) {
                    console.log('Record added successfully');
                } else {
                    console.error('Failed to add record');
                }
            }
        } catch (error) {
            console.error('Error saving patient name:', error);
        }
        // Call the save function with modifiedName and invoiceId
        setModifiedDisplayNames({
            ...modifiedDisplayNames,
            [invoiceId]: { patient_name:patientName, patient_id:patientID }
        });
        setEditingInvoiceId(null);
    };
    
    const handlePatientNameChange = (invoiceId, newName) => {
        setModifiedPatientNames({ ...modifiedPatientNames, [invoiceId]: newName });
    };

    const handlePatientIDChange = (invoiceId, newName) => {
        setModifiedPatientIDs({ ...modifiedPatientIDs, [invoiceId]: newName });
    };

    const handleCancelEdit = async (invoiceId) => {
        // Remove modified data for the given invoiceId
        const modifiedNamesCopy = { ...modifiedPatientNames };
        const modifiedIDsCopy = { ...modifiedPatientIDs };
        const modifiedDisplayNamesCopy = { ...modifiedDisplayNames};
        delete modifiedNamesCopy[invoiceId];
        delete modifiedIDsCopy[invoiceId];
        delete modifiedDisplayNamesCopy[invoiceId];
        setModifiedPatientNames(modifiedNamesCopy);
        setModifiedPatientIDs(modifiedIDsCopy);
        setModifiedDisplayNames(modifiedDisplayNamesCopy);
    
        // Cancel editing mode
        setEditingInvoiceId(null);

        try {
            // Make a DELETE request to the delete endpoint
            await axios.delete(`${serverURL}/sh_invoice_delete/${invoiceId}`);
            console.log('Record deleted successfully');
        } catch (error) {
            console.error('Error deleting record:', error);
        }
      };

      const getDisplayName = (invoiceId, unEdited) => {
        if(modifiedDisplayNames[invoiceId]) {
            let patientID = modifiedDisplayNames[invoiceId].patient_id;
            const patientName = modifiedDisplayNames[invoiceId].patient_name;

            if (patientID && !patientID.includes('ID')) {
                patientID = 'ID-' + patientID;
            }
            return patientName + " (" + patientID + ")";
        } else {
            return unEdited;
        }
      }
  return (
    <div className="printWrapper">
        <div className="print-button-container">
          <button className='printButton' onClick={handlePrint} disabled={isLoading}>Print</button>
          <button className='exportButton' onClick={handleExport} disabled={isLoading}>Export to Excel</button>
        </div>
        <div className="separator"></div>
        <div className="list-invoices">
        <div id="summaryHeader">
            <h2 className="hospital-name">Shuman Hospital</h2><div class="address">
                <h3>15-17 Pultney Street Freetown</h3>
                <h3>Smdcenter.Info@gmail.com</h3>
                <h3>Tel: 022-224224</h3>
            </div>
            <h2 class="customer-name">{customerName}</h2>
            <h3 className='dateRange'>{dateRangeText}</h3>
        </div>
            {progress == 100 ? (
            <>
            <div id="totalSummary">
                <table className='summaryTotalsTable'>
                    <tbody>
                        <tr>
                            <td>Total:</td><td>&nbsp;&nbsp;&nbsp;&nbsp;<b>{(+overallTotal.toFixed(1) + +totalDiscount.toFixed(1)*(-1))}</b></td>
                        </tr>
                        <tr>
                            <td>Discount:</td><td>(-)&nbsp;<b>{totalDiscount.toFixed(1)*(-1)}</b></td>
                        </tr>
                        <tr>
                            <td>Total Due:</td><td>&nbsp;&nbsp;&nbsp;&nbsp;<b>{overallTotal.toFixed(1)}</b></td>
                        </tr>
                        <tr>
                            <td>Number of Invoices:</td><td>{invoices.length}</td>
                        </tr>
                    </tbody>
                </table>
            </div>
                
            <table id="mainExportTable" className="summaryTable">
            <thead>
                <tr>
                    <th className='patientCellHeader'>Patient Name</th>
                    <th className='dateCellHeader'>Date</th>
                    <th className='itemsCellHeader'>Detail</th>
                    <th className='dateTotalCellHeader'>Total</th> 
                    <th className='dateTotalCellHeader'>After Discount</th> 
                </tr>
            </thead>
            <tbody>
                {Object.keys(groupedInvoices).map((key) => (
                <tr className="patientRecord" key={key}>
                {editingInvoiceId === groupedInvoices[key][0].invoice.invoice_id ? (
                    // Render input field in editing mode
                    <td className="patientCell">
                        Name:<input
                        type="text"
                        value={modifiedPatientNames[groupedInvoices[key][0].invoice.invoice_id] || ''}
                        onChange={(e) => handlePatientNameChange(groupedInvoices[key][0].invoice.invoice_id, e.target.value)}
                    />
                    ID:<input
                        type="text"
                        value={modifiedPatientIDs[groupedInvoices[key][0].invoice.invoice_id] || ''}
                        onChange={(e) => handlePatientIDChange(groupedInvoices[key][0].invoice.invoice_id, e.target.value)}
                    />
                        <div className='editButtons'>
                            <button onClick={() => handleSavePatientName(groupedInvoices[key][0].invoice.invoice_id)}>Save</button>
                            <button onClick={() => handleCancelPatientNameEdit(groupedInvoices[key][0].invoice.invoice_id)}>Cancel</button>
                        </div>
                    </td>
)                   : (
                    <td className="patientCell">
                        <a href={`/invoice/${groupedInvoices[key][0].invoice.invoice_id}`} target="_blank">{getDisplayName(groupedInvoices[key][0].invoice.invoice_id,key)}</a>
                        
                        {modifiedDisplayNames[groupedInvoices[key][0].invoice.invoice_id] ? (
                            <>
                            <FaPencilAlt className="editIcon" onClick={() => handleEditPatientName(groupedInvoices[key][0].invoice.invoice_id)} />
                            <FaTimes className="cancelIcon" onClick={() => handleCancelEdit(groupedInvoices[key][0].invoice.invoice_id)} />
                            </>
                        ):(
                            <FaPencilAlt className="editIcon" onClick={() => handleEditPatientName(groupedInvoices[key][0].invoice.invoice_id)} />
                        )}
                    </td>
                    )}    
                    <td colSpan={4} className="invoiceCell">
                        <table className='itemsTable'>

                            <tbody>
                            {groupedInvoices[key].map((item, index) => (
                            <tr className='visitRecord' key={index}>
                                <td className="dateCell">
                                {item.invoice.date}
                                </td>

                                <td className="itemsCell">
                                <InvoiceItem invoice={item.invoice} lineItems={item.lineItems} />
                                </td>
                                
                                <td className='dateTotalCell'>
                                {item.invoice.adjustment*(-1) + item.invoice.total}
                                </td>                                
                                
                                <td className='dateTotalCell'>
                                {item.invoice.total}
                                </td>
                            </tr>
                            ))}
                            </tbody>
                        </table>
                    </td>
                </tr>
                ))}
                </tbody>
            </table>
            </>
            ) : (
            <LoadingSpinner progress={progress} /> // Pass the progress to the LoadingSpinner component
            )}
        </div>
    </div>
  );
};

export default CustomerInvoices;
